import { html } from 'lit';
import type { LandingContent, LandingTestimonialItem } from '../../shared/content/landingContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

const testimonialsFromContent = (content: LandingContent): LandingTestimonialItem[] =>
  content.story.testimonials
    ? content.story.testimonials
    : [
        {
          quote: content.story.quote,
          author: content.story.author,
          role: content.story.role,
          logo: content.story.partnerLogo,
          logoAlt: 'Partnerlogo',
        },
      ];

// The layout stays content-driven so the same speech-bubble card works with one or several editor entries.
export const storySection = (content: LandingContent, editor?: LandingEditorRenderOptions) => {
  const testimonials = testimonialsFromContent(content);
  const testimonialColumns = editor?.project.elementFrames['story.testimonials']?.testimonialColumns;
  const testimonialTrackClass = [
    'vp-testimonial-track',
    testimonials.length === 1 ? 'is-single' : '',
    testimonialColumns ? `has-manual-layout has-${testimonialColumns}-columns` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return html`
    <section
      class=${editorNodeClass(editor, 'section.story', 'vp-section vp-story-section vp-story-section-testimonial-only')}
      style=${editorNodeStyle(editor, 'section.story')}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'section.story', event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, 'section.story', event)}
    >
      <div class="vp-shell">
        <vp-testimonials
          class=${editorNodeClass(editor, 'story.testimonials', 'vp-testimonials')}
          style=${editorNodeStyle(editor, 'story.testimonials')}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'story.testimonials', event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, 'story.testimonials', event)}
        >
          <div class=${testimonialTrackClass}>
            ${testimonials.map((testimonial, index) => {
              const itemId = `story.testimonial.${index}`;
              const isEditorSelected =
                editor?.selectedElementId === itemId || editor?.selectedElementId?.startsWith(`${itemId}.`);
              return html`
                <article
                  class=${editorNodeClass(
                    editor,
                    itemId,
                    `vp-testimonial-card reveal-card ${index === 0 ? 'is-active' : ''} ${isEditorSelected ? 'is-editor-selected' : ''}`,
                  )}
                  style=${editorNodeStyle(editor, itemId)}
                  data-index=${index}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, itemId, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, itemId, event)}
                >
                  ${testimonial.image
                    ? html`<img
                        class=${editorNodeClass(editor, `${itemId}.image`, 'vp-testimonial-image')}
                        style=${editorNodeStyle(editor, `${itemId}.image`)}
                        src=${testimonial.image}
                        alt=${testimonial.imageAlt ?? testimonial.author}
                        loading="lazy"
                        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.image`, event)}
                        @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.image`, event)}
                      />`
                    : html`<span class="vp-testimonial-image-placeholder icon-[material-symbols--format-quote]"></span>`}
                  <div class="vp-testimonial-copy">
                    <blockquote
                      class=${editorNodeClass(editor, `${itemId}.quote`)}
                      style=${editorNodeStyle(editor, `${itemId}.quote`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.quote`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.quote`, event)}
                    >
                      ${testimonial.quote}
                    </blockquote>
                    <footer>
                      ${testimonial.logo
                        ? html`<img
                            class=${editorNodeClass(editor, `${itemId}.logo`, 'vp-testimonial-logo')}
                            style=${editorNodeStyle(editor, `${itemId}.logo`)}
                            src=${testimonial.logo}
                            alt=${testimonial.logoAlt ?? `${testimonial.author} Logo`}
                            loading="lazy"
                            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.logo`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.logo`, event)}
                          />`
                        : ''}
                      <span class="vp-testimonial-attribution">
                        <strong
                          class=${editorNodeClass(editor, `${itemId}.author`)}
                          style=${editorNodeStyle(editor, `${itemId}.author`)}
                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.author`, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.author`, event)}
                          >${testimonial.author}</strong
                        >
                        <span
                          class=${editorNodeClass(editor, `${itemId}.role`)}
                          style=${editorNodeStyle(editor, `${itemId}.role`)}
                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.role`, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.role`, event)}
                          >${testimonial.role}</span
                        >
                      </span>
                    </footer>
                  </div>
                </article>
              `;
            })}
            ${editor?.mode === 'editor'
              ? html`
                  <button
                    type="button"
                    class="vp-editor-testimonial-add"
                    aria-label="Neues Testimonial hinzufügen"
                    @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
                    @click=${(event: MouseEvent) => {
                      event.preventDefault();
                      event.stopPropagation();
                      editor.onAddTestimonial?.();
                    }}
                  >
                    <span class="icon-[material-symbols--add-circle-outline]" aria-hidden="true"></span>
                    <span>Testimonial hinzufügen</span>
                  </button>
                `
              : ''}
          </div>
        </vp-testimonials>
      </div>
    </section>
  `;
};

class VpTestimonialsElement extends HTMLElement {
  private cards: HTMLElement[] = [];
  private controls: HTMLElement[] = [];

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll<HTMLElement>('.vp-testimonial-card'));
    this.controls = Array.from(this.querySelectorAll<HTMLElement>('.vp-testimonial-control'));
    this.controls.forEach((control, index) => {
      control.addEventListener('click', this.onControlClick);
      control.dataset.index = String(index);
    });
  }

  disconnectedCallback() {
    this.controls.forEach(control => control.removeEventListener('click', this.onControlClick));
  }

  private onControlClick = (event: Event) => {
    const index = Number((event.currentTarget as HTMLElement).dataset.index);
    if (Number.isNaN(index)) return;
    this.setActive(index);
    this.cards[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  private setActive(index: number) {
    this.cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
    this.controls.forEach((control, controlIndex) => control.classList.toggle('is-active', controlIndex === index));
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('vp-testimonials')) {
  customElements.define('vp-testimonials', VpTestimonialsElement);
}
